/**
 * Client-side hero greeting — assembles a contextual greeting from local data.
 * Zero API calls. All template-based with enough variety to feel natural.
 */

import type { Project } from "@/lib/api";

// ── Types ───────────────────────────────────────────────────

type TimeOfDay = "morning" | "afternoon" | "evening" | "late";

export interface HeroSuggestion {
  id: string;
  label: string;
  action: string;
  payload: Record<string, unknown>;
  style?: "primary" | "secondary";
}

export interface HeroGreetingResult {
  text: string;
  suggestions: HeroSuggestion[];
}

export interface HeroGreetingInput {
  projects: Project[];
  hasGitHub: boolean;
  seed?: number;
}

// ── Time of day ─────────────────────────────────────────────

function getTimeOfDay(hour: number = new Date().getHours()): TimeOfDay {
  if (hour < 5) return "late";
  if (hour < 12) return "morning";
  if (hour < 17) return "afternoon";
  if (hour < 21) return "evening";
  return "late";
}

// ── Seeded random picker ────────────────────────────────────

function seededPick<T>(items: T[], seed: number): T {
  return items[Math.abs(seed) % items.length]!;
}

// ── Openers by time of day ──────────────────────────────────

const OPENERS: Record<TimeOfDay, string[]> = {
  morning: [
    "Morning.",
    "Hey, morning.",
    "Good morning.",
    "Early start today.",
    "Top of the morning.",
  ],
  afternoon: [
    "Hey.",
    "Afternoon.",
    "Hey there.",
    "What's good.",
  ],
  evening: [
    "Evening.",
    "Hey.",
    "Hey there.",
    "Winding down or just getting started?",
  ],
  late: [
    "Late one.",
    "Hey.",
    "Still up?",
    "Burning the midnight oil?",
    "Night owl mode.",
  ],
};

// ── "Yes" label varieties ───────────────────────────────────

const YES_LABELS: string[] = [
  "Yeah, let's go",
  "Let's do it",
  "Jump back in",
  "Pick it up",
  "Yeah, continue",
  "Back to it",
  "Let's roll",
  "On it",
];

// ── Templates: has recent task + project ────────────────────
// isQuestion: whether the greeting ends with a yes/no question
//
// All templates here MUST read naturally with both:
//   - noun phrases:  "the auth refactor"
//   - gerund forms:  "fixing the websocket disconnect"
// Templates that demand a strict noun-phrase shape (e.g. "You were working
// on ___ in p") have been removed because the upstream task data is not
// reliably grammatical. normalizeTaskForGreeting() does the heavy lifting,
// and these surviving templates are forgiving enough that even a borderline
// task still reads OK.

interface TaskTemplate {
  fn: (opener: string, project: string, task: string) => string;
  isQuestion: boolean;
}

const TASK_TEMPLATES: TaskTemplate[] = [
  { fn: (o, p, t) => `${o} Last time in ${p}: ${t}. Want to pick that back up?`, isQuestion: true },
  { fn: (o, p, t) => `${o} Left off in ${p} — ${t}. Continue, or something new?`, isQuestion: true },
  { fn: (_o, p, t) => `Welcome back. ${p} — ${t}. Want to keep going?`, isQuestion: true },
  { fn: (_o, p, t) => `So, ${p}. Last you were there: ${t}. Jump back in?`, isQuestion: true },
  { fn: (o, p, t) => `${o} Quick recap on ${p}: ${t}. Continue?`, isQuestion: true },
  { fn: (_o, p, t) => `Last time was ${p} — ${t}. Back at it, or starting fresh?`, isQuestion: true },
  { fn: (o, p, t) => `${o} ${p} — ${t}. Pick up where you left off?`, isQuestion: true },
  { fn: (_o, p, t) => `Last note from ${p}: ${t}. Want to keep going?`, isQuestion: true },
  { fn: (o, p, t) => `${o} ${p}, mid-flow: ${t}. Continue?`, isQuestion: true },
  { fn: (_o, p, t) => `Right where you left off in ${p}: ${t}.`, isQuestion: false },
];

// ── Templates: single project, no task ──────────────────────

interface SingleProjectTemplate {
  fn: (opener: string, project: string) => string;
  isQuestion: boolean;
}

const PROJECT_NO_TASK_SINGLE: SingleProjectTemplate[] = [
  { fn: (o, p) => `${o} Want to jump into ${p}?`, isQuestion: true },
  { fn: (o, p) => `${o} Ready to work on ${p}?`, isQuestion: true },
  { fn: (_o, p) => `Hey. ${p} is waiting. Want to dive in?`, isQuestion: true },
  { fn: (o) => `${o} What are you working on?`, isQuestion: false },
  { fn: (o, p) => `${o} ${p} — shall we?`, isQuestion: true },
  { fn: (_o, p) => `Back again. Want to hop into ${p}?`, isQuestion: true },
  { fn: (o, p) => `${o} Got ${p} loaded up. What are we doing?`, isQuestion: false },
];

// ── Templates: multiple projects, no task ───────────────────
// All open-ended — suggestions will show project names

const PROJECT_NO_TASK_MULTI: ((opener: string) => string)[] = [
  (o) => `${o} What are you working on?`,
  (o) => `${o} What should we work on?`,
  () => "Hey. What's up?",
  (o) => `${o} What are we building?`,
  (o) => `${o} Got a few projects going. Which one?`,
  () => "Hey. Where are we headed today?",
  (o) => `${o} What's the move?`,
  () => "What do you want to dig into?",
];

// ── Templates: no projects ──────────────────────────────────

const NO_PROJECTS_WITH_GITHUB: string[] = [
  "Hey. Looks like you're just getting started. Once you have a project set up, we can get to work.",
  "Welcome to Daydra. No projects yet — once you create one, this is where we'll build.",
  "Hey. GitHub's connected, nice. Set up a project and we'll get rolling.",
  "All set on the GitHub side. Create a project whenever you're ready and we'll jump in.",
];

const NO_PROJECTS_NO_GITHUB: string[] = [
  "Hey. Welcome to Daydra. Connect your GitHub to get started, or tell me what you're working on.",
  "Welcome to Daydra. You can connect your GitHub to get started, or just tell me what you're building.",
  "Hey there. First step is connecting your GitHub — say \"connect github\" and I'll walk you through it.",
  "Welcome. Once you connect GitHub, I can start working with your repos. Want to set that up?",
];

// ── Tips ────────────────────────────────────────────────────

const TIPS: string[] = [
  "By the way, you can ask me to scaffold entire features — files, tests, the works.",
  "Tip: you can paste error logs straight into the chat and I'll trace them.",
  "You know you can ask me to review a PR or diff, right? Just drop the link.",
  "Fun fact — I can run shell commands for you. Just say what you need.",
  "If you ever want a quick summary of recent changes, just ask for a changelog.",
  "Need to refactor something? I can handle multi-file changes in one go.",
  "You can ask me to explain any part of the codebase. I'll walk you through it.",
  "Quick reminder — I can set up new projects from a GitHub repo in seconds.",
  "I can write and run tests for you. Just point me at what needs coverage.",
  "If something breaks, paste the stack trace and I'll track it down.",
  "You can ask me to search your codebase for anything — patterns, usages, dead code.",
  "I can help with git operations too — branching, merging, resolving conflicts.",
];

// ── Helpers ─────────────────────────────────────────────────

const STALE_DAYS = 7;

function isStale(updatedAt: string | null | undefined): boolean {
  if (!updatedAt) return true;
  const updated = new Date(updatedAt).getTime();
  const now = Date.now();
  return now - updated > STALE_DAYS * 24 * 60 * 60 * 1000;
}

// ── Task normalization (mirrors backend _normalize_task_for_display) ─
//
// Defense in depth: legacy `last_task` rows in the DB were written before the
// backend normalizer existed, so we re-validate on read. If the task can't be
// shaped into something that templates well, we return null and the caller
// falls through to the no-task greeting branches.
//
// Kept deliberately in lockstep with backend/app/orchestrator/context_manager.py
// (_normalize_task_for_display). When updating one, update the other.

const TASK_LEADING_FILLER = [
  "i would like you to",
  "i'd like you to",
  "i would like to",
  "i'd like to",
  "i'm going to",
  "i am going to",
  "i want you to",
  "i want to",
  "i need you to",
  "i need to",
  "we should",
  "we need to",
  "we have to",
  "i should",
  "i have to",
  "i'll go ahead and",
  "i will go ahead and",
  "go ahead and",
  "could you please",
  "can you please",
  "could you",
  "can you",
  "please",
  "let's go ahead and",
  "let's",
  "let me",
  "i'll",
  "i will",
  "just",
];

const IMPERATIVE_VERBS = new Set([
  "add", "build", "check", "clean", "convert", "create", "debug",
  "delete", "deploy", "drop", "extract", "figure", "find", "finish",
  "fix", "hook", "implement", "install", "investigate", "look",
  "make", "migrate", "move", "patch", "port", "pull", "push",
  "refactor", "remove", "rename", "replace", "research", "restart",
  "review", "run", "scaffold", "set", "ship", "split", "swap",
  "test", "try", "tweak", "update", "upgrade", "wire", "write",
]);

const LEADING_PRONOUNS = new Set(["i", "you", "we", "they", "he", "she", "it"]);

const GERUND_IRREGULAR: Record<string, string> = {
  run: "running",
  set: "setting",
  ship: "shipping",
  swap: "swapping",
  drop: "dropping",
  hook: "hooking",
};

function toGerund(verb: string): string {
  const v = verb.toLowerCase();
  if (v in GERUND_IRREGULAR) return GERUND_IRREGULAR[v]!;
  if (v.endsWith("ie")) return v.slice(0, -2) + "ying";
  if (v.endsWith("e") && !v.endsWith("ee")) return v.slice(0, -1) + "ing";
  return v + "ing";
}

export function normalizeTaskForGreeting(text: string | null | undefined): string | null {
  if (!text) return null;

  let cleaned = text.trim().replace(/^["'`]+|["'`]+$/g, "");
  cleaned = cleaned.replace(/[.!,;:\s]+$/g, "");

  if (!cleaned) return null;
  if (cleaned.includes("\n") || cleaned.includes("?")) return null;

  // Strip leading filler iteratively — chains like "please can you" are common.
  let lowered = cleaned.toLowerCase();
  let changed = true;
  while (changed) {
    changed = false;
    for (const phrase of TASK_LEADING_FILLER) {
      if (lowered === phrase || lowered.startsWith(phrase + " ")) {
        cleaned = cleaned.slice(phrase.length).replace(/^\s+/, "");
        lowered = cleaned.toLowerCase();
        changed = true;
        break;
      }
    }
  }

  if (!cleaned) return null;

  const firstWordMatch = cleaned.match(/^[A-Za-z']+/);
  if (!firstWordMatch) return null;
  const firstWord = firstWordMatch[0].toLowerCase();
  if (LEADING_PRONOUNS.has(firstWord)) return null;

  if (IMPERATIVE_VERBS.has(firstWord)) {
    cleaned = toGerund(firstWord) + cleaned.slice(firstWordMatch[0].length);
  }

  cleaned = cleaned[0]!.toLowerCase() + cleaned.slice(1);

  if (cleaned.length < 6 || cleaned.length > 100) return null;

  return cleaned;
}

function shortenTask(text: string, maxLen = 80): string {
  const trimmed = text.trim();
  if (trimmed.length <= maxLen) return trimmed;

  const firstSentence = trimmed.split(/(?<=[.!?])\s/)[0] ?? trimmed;
  if (firstSentence.length <= maxLen) return firstSentence.replace(/\.$/, "");

  const truncated = trimmed.slice(0, maxLen);
  const breaks = [...truncated.matchAll(/[,;—–-]\s|\.\s|\s/g)];
  if (breaks.length > 0) {
    const lastBreak = breaks[breaks.length - 1]!;
    const cut = lastBreak.index!;
    if (cut > maxLen * 0.4) {
      return truncated.slice(0, cut).replace(/[.,;:—–\s-]+$/, "");
    }
  }
  return truncated.replace(/[.,;:—–\s-]+$/, "");
}

function truncName(name: string, max = 20): string {
  return name.length > max ? name.slice(0, max - 1) + "\u2026" : name;
}

function projectSuggestion(
  p: Project, style: "primary" | "secondary" = "secondary",
): HeroSuggestion {
  return {
    id: `switch-${p.id.slice(0, 8)}`,
    label: truncName(p.name),
    action: "switch_project",
    payload: { project_id: p.id, project_name: p.name },
    style,
  };
}

function maybeTip(text: string, seed: number): string {
  if ((seed % 20) < 3) {
    return text + " " + seededPick(TIPS, seed >> 5);
  }
  return text;
}

// ── Main export ─────────────────────────────────────────────

export function buildHeroGreeting({
  projects,
  hasGitHub,
  seed = Date.now(),
}: HeroGreetingInput): HeroGreetingResult {
  const tod = getTimeOfDay();
  const opener = seededPick(OPENERS[tod], seed);

  // ── No projects ──────────────────────────────────────────
  if (projects.length === 0) {
    if (hasGitHub) {
      return { text: seededPick(NO_PROJECTS_WITH_GITHUB, seed), suggestions: [] };
    }
    return { text: seededPick(NO_PROJECTS_NO_GITHUB, seed), suggestions: [] };
  }

  // ── Has recent task + project ────────────────────────────
  // We re-normalize on read because legacy DB rows may predate the backend
  // normalizer. If the task can't be shaped into something that templates
  // cleanly, fall through to the no-task branches below.
  const recentWithTask = projects.find(
    (p) => p.last_task && !isStale(p.last_task_updated_at),
  );
  const normalizedTask = recentWithTask?.last_task
    ? normalizeTaskForGreeting(shortenTask(recentWithTask.last_task))
    : null;

  if (recentWithTask && normalizedTask) {
    const task = normalizedTask;
    const projectName = recentWithTask.name;
    const tmpl = seededPick(TASK_TEMPLATES, seed >> 3);
    const text = maybeTip(tmpl.fn(opener, projectName, task), seed);

    const suggestions: HeroSuggestion[] = [];
    if (tmpl.isQuestion) {
      suggestions.push({
        id: `yes-${recentWithTask.id.slice(0, 8)}`,
        label: seededPick(YES_LABELS, seed >> 4),
        action: "switch_project",
        payload: { project_id: recentWithTask.id, project_name: recentWithTask.name },
        style: "primary",
      });
    } else {
      // Statement greeting — show "Open {project}" instead of "Yes"
      suggestions.push({
        id: `open-${recentWithTask.id.slice(0, 8)}`,
        label: `Open ${truncName(recentWithTask.name, 16)}`,
        action: "switch_project",
        payload: { project_id: recentWithTask.id, project_name: recentWithTask.name },
        style: "primary",
      });
    }

    return { text, suggestions };
  }

  // ── Single project, no task ──────────────────────────────
  if (projects.length === 1) {
    const p = projects[0]!;
    const tmpl = seededPick(PROJECT_NO_TASK_SINGLE, seed >> 2);
    const text = maybeTip(tmpl.fn(opener, p.name), seed);

    const suggestions: HeroSuggestion[] = [];
    if (tmpl.isQuestion) {
      suggestions.push({
        id: `yes-${p.id.slice(0, 8)}`,
        label: seededPick(YES_LABELS, seed >> 6),
        action: "switch_project",
        payload: { project_id: p.id, project_name: p.name },
        style: "primary",
      });
    } else {
      suggestions.push({
        id: `open-${p.id.slice(0, 8)}`,
        label: `Open ${truncName(p.name, 16)}`,
        action: "switch_project",
        payload: { project_id: p.id, project_name: p.name },
        style: "primary",
      });
    }

    return { text, suggestions };
  }

  // ── Multiple projects, no task ───────────────────────────
  // Show project names as buttons — never a "yes" button
  const template = seededPick(PROJECT_NO_TASK_MULTI, seed >> 2);
  const text = maybeTip(template(opener), seed);

  const suggestions: HeroSuggestion[] = projects.slice(0, 3).map((p, i) =>
    projectSuggestion(p, i === 0 ? "primary" : "secondary"),
  );

  return { text, suggestions };
}
